/**
 * PM Module - Route Definitions
 *
 * All routes for Project Management module
 * These are spread into the central router at /src/app/router.tsx
 *
 * URL is the source of truth:
 * - When URL changes, canvas state is updated to match
 * - When canvas changes (via sidebar), URL is updated via pmNavigation
 */

import { Suspense, lazy, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { usePMWorkspaceStore, type PMCanvas } from './stores'
import { AgentLoader } from '@/components/ui/AgentLoader'
import { resetNavigationFlag } from './utils/pmNavigation'

// ============================================
// LAZY LOAD WORKSPACE
// ============================================

const PMEmbeddedWorkspace = lazy(() =>
  import('./workspace/PMEmbeddedWorkspace').then((m) => ({ default: m.PMEmbeddedWorkspace }))
)

// ============================================
// LOADING FALLBACK
// ============================================

function PMLoader() {
  return (
    <div className="h-[calc(100vh-12rem)] flex items-center justify-center">
      <AgentLoader size="lg" text="Loading Project Management..." />
    </div>
  )
}

// ============================================
// CANVAS MAPPING
// ============================================

const canvasMap: Record<string, PMCanvas> = {
  // Dashboard
  dashboard: 'dashboard',
  '': 'dashboard',

  // Manager (unified project & task view)
  manager: 'manager',

  // Projects
  projects: 'projects',
  'my-projects': 'my-projects',

  // Tasks
  tasks: 'tasks',
  'my-tasks': 'my-tasks',
  'assigned-by-me': 'assigned-by-me',

  // Agile
  sprints: 'sprints',
  backlog: 'backlog',

  // Timeline
  milestones: 'milestones',
  gantt: 'gantt',
  timeline: 'timeline',
  deadlines: 'deadlines',

  // Calendar (my-calendar now redirects to calendar with unified view)
  calendar: 'calendar',
  'my-calendar': 'calendar',

  // Management
  resources: 'resources',
  'time-tracking': 'time-tracking',
  risks: 'risks',
  clients: 'clients',
  // HR-PM Integration disabled via feature flag - uncomment when ready
  // 'hrpm-integration': 'hrpm-integration',

  // Reports
  analytics: 'analytics',
  workflows: 'workflows',

  // Communication
  chat: 'chat',
  feed: 'feed',
}

// ============================================
// WORKSPACE WRAPPER
// ============================================

function PMWorkspaceWrapper({ canvas }: { canvas?: string }) {
  const [isHydrated, setIsHydrated] = useState(false)

  // Wait for zustand persist to complete rehydration before syncing canvas
  // This prevents race condition where effect runs before persisted state is loaded
  useEffect(() => {
    // Check if already hydrated
    if (usePMWorkspaceStore.persist.hasHydrated()) {
      setIsHydrated(true)
    } else {
      // Wait for hydration to complete
      const unsubscribe = usePMWorkspaceStore.persist.onFinishHydration(() => {
        setIsHydrated(true)
      })
      return () => unsubscribe()
    }
  }, [])

  // Sync canvas state with URL after hydration is complete
  useEffect(() => {
    if (!isHydrated) return

    // URL is the source of truth - sync canvas state to match URL
    const currentCanvas = usePMWorkspaceStore.getState().activeCanvas
    const targetCanvas = canvas && canvasMap[canvas] ? canvasMap[canvas] : 'dashboard'

    // Only update if different to avoid unnecessary re-renders
    if (currentCanvas !== targetCanvas) {
      // Directly set state without triggering URL navigation (we're already on correct URL)
      usePMWorkspaceStore.setState({
        previousCanvas: currentCanvas,
        activeCanvas: targetCanvas,
        selectedTaskIds: [],
      })
    }

    // Reset navigation flag after URL-triggered canvas update
    resetNavigationFlag()
  }, [canvas, isHydrated])

  return (
    <Suspense fallback={<PMLoader />}>
      <PMEmbeddedWorkspace />
    </Suspense>
  )
}

// Project Detail Wrapper
// Sets canvas and selection from URL without triggering URL update (already on correct URL)
function ProjectDetailWrapper() {
  const { projectId } = useParams<{ projectId: string }>()
  const [isHydrated, setIsHydrated] = useState(false)

  // Wait for zustand persist to complete rehydration
  useEffect(() => {
    if (usePMWorkspaceStore.persist.hasHydrated()) {
      setIsHydrated(true)
    } else {
      const unsubscribe = usePMWorkspaceStore.persist.onFinishHydration(() => {
        setIsHydrated(true)
      })
      return () => unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!isHydrated || !projectId) return

    const currentCanvas = usePMWorkspaceStore.getState().activeCanvas
    const selectedProject = usePMWorkspaceStore.getState().selectedProject

    // Only update if needed
    if (currentCanvas !== 'project-detail' || selectedProject?.id !== projectId) {
      usePMWorkspaceStore.setState({
        selectedProject: { type: 'project', id: projectId },
        activeCanvas: 'project-detail',
        isDetailPanelOpen: true,
      })
    }
    resetNavigationFlag()
  }, [projectId, isHydrated])

  return (
    <Suspense fallback={<PMLoader />}>
      <PMEmbeddedWorkspace />
    </Suspense>
  )
}

// Task Detail Wrapper
// Sets canvas and selection from URL without triggering URL update (already on correct URL)
function TaskDetailWrapper() {
  const { taskId } = useParams<{ taskId: string }>()
  const [isHydrated, setIsHydrated] = useState(false)

  // Wait for zustand persist to complete rehydration
  useEffect(() => {
    if (usePMWorkspaceStore.persist.hasHydrated()) {
      setIsHydrated(true)
    } else {
      const unsubscribe = usePMWorkspaceStore.persist.onFinishHydration(() => {
        setIsHydrated(true)
      })
      return () => unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!isHydrated || !taskId) return

    const currentCanvas = usePMWorkspaceStore.getState().activeCanvas
    const selectedTask = usePMWorkspaceStore.getState().selectedTask

    // Only update if needed
    if (currentCanvas !== 'task-detail' || selectedTask?.id !== taskId) {
      usePMWorkspaceStore.setState({
        selectedTask: { type: 'task', id: taskId },
        activeCanvas: 'task-detail',
        isDetailPanelOpen: true,
      })
    }
    resetNavigationFlag()
  }, [taskId, isHydrated])

  return (
    <Suspense fallback={<PMLoader />}>
      <PMEmbeddedWorkspace />
    </Suspense>
  )
}

// Sprint Detail Wrapper
// Sets canvas and selection from URL without triggering URL update (already on correct URL)
function SprintDetailWrapper() {
  const { sprintId } = useParams<{ sprintId: string }>()
  const [isHydrated, setIsHydrated] = useState(false)

  // Wait for zustand persist to complete rehydration
  useEffect(() => {
    if (usePMWorkspaceStore.persist.hasHydrated()) {
      setIsHydrated(true)
    } else {
      const unsubscribe = usePMWorkspaceStore.persist.onFinishHydration(() => {
        setIsHydrated(true)
      })
      return () => unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!isHydrated || !sprintId) return

    const currentCanvas = usePMWorkspaceStore.getState().activeCanvas
    const selectedSprint = usePMWorkspaceStore.getState().selectedSprint

    // Only update if needed
    if (currentCanvas !== 'sprint-detail' || selectedSprint?.id !== sprintId) {
      usePMWorkspaceStore.setState({
        selectedSprint: { type: 'sprint', id: sprintId },
        activeCanvas: 'sprint-detail',
      })
    }
    resetNavigationFlag()
  }, [sprintId, isHydrated])

  return (
    <Suspense fallback={<PMLoader />}>
      <PMEmbeddedWorkspace />
    </Suspense>
  )
}

// ============================================
// PM ROUTES (under /dashboard/pm/*)
// ============================================

export const pmRoutes: RouteObject[] = [
  // PM Dashboard (main entry)
  {
    path: 'pm',
    element: <PMWorkspaceWrapper />,
  },

  // Manager (unified project & task view)
  {
    path: 'pm/manager',
    element: <PMWorkspaceWrapper canvas="manager" />,
  },

  // Projects
  {
    path: 'pm/projects',
    element: <PMWorkspaceWrapper canvas="projects" />,
  },
  {
    path: 'pm/projects/:projectId',
    element: <ProjectDetailWrapper />,
  },
  {
    path: 'pm/my-projects',
    element: <PMWorkspaceWrapper canvas="my-projects" />,
  },

  // Tasks
  {
    path: 'pm/tasks',
    element: <PMWorkspaceWrapper canvas="tasks" />,
  },
  {
    path: 'pm/tasks/:taskId',
    element: <TaskDetailWrapper />,
  },
  {
    path: 'pm/my-tasks',
    element: <PMWorkspaceWrapper canvas="my-tasks" />,
  },
  {
    path: 'pm/assigned-by-me',
    element: <PMWorkspaceWrapper canvas="assigned-by-me" />,
  },

  // Agile
  {
    path: 'pm/sprints',
    element: <PMWorkspaceWrapper canvas="sprints" />,
  },
  {
    path: 'pm/sprints/:sprintId',
    element: <SprintDetailWrapper />,
  },
  {
    path: 'pm/backlog',
    element: <PMWorkspaceWrapper canvas="backlog" />,
  },

  // Timeline
  {
    path: 'pm/milestones',
    element: <PMWorkspaceWrapper canvas="milestones" />,
  },
  {
    path: 'pm/gantt',
    element: <PMWorkspaceWrapper canvas="gantt" />,
  },
  {
    path: 'pm/timeline',
    element: <PMWorkspaceWrapper canvas="timeline" />,
  },
  {
    path: 'pm/deadlines',
    element: <PMWorkspaceWrapper canvas="deadlines" />,
  },

  // Calendar
  {
    path: 'pm/calendar',
    element: <PMWorkspaceWrapper canvas="calendar" />,
  },
  {
    path: 'pm/my-calendar',
    element: <PMWorkspaceWrapper canvas="my-calendar" />,
  },

  // Communication
  {
    path: 'pm/chat',
    element: <PMWorkspaceWrapper canvas="chat" />,
  },
  {
    path: 'pm/feed',
    element: <PMWorkspaceWrapper canvas="feed" />,
  },

  // Management
  {
    path: 'pm/resources',
    element: <PMWorkspaceWrapper canvas="resources" />,
  },
  {
    path: 'pm/time-tracking',
    element: <PMWorkspaceWrapper canvas="time-tracking" />,
  },
  {
    path: 'pm/risks',
    element: <PMWorkspaceWrapper canvas="risks" />,
  },
  {
    path: 'pm/clients',
    element: <PMWorkspaceWrapper canvas="clients" />,
  },
  // HR-PM Integration disabled via feature flag - uncomment when ready
  // {
  //   path: 'pm/hrpm-integration',
  //   element: <PMWorkspaceWrapper canvas="hrpm-integration" />,
  // },

  // Reports
  {
    path: 'pm/analytics',
    element: <PMWorkspaceWrapper canvas="analytics" />,
  },
  {
    path: 'pm/workflows',
    element: <PMWorkspaceWrapper canvas="workflows" />,
  },

  // Direct paths (alternative access without /pm prefix)
  {
    path: 'projects',
    element: <PMWorkspaceWrapper canvas="projects" />,
  },
  {
    path: 'projects/:projectId',
    element: <ProjectDetailWrapper />,
  },
]

// ============================================
// PM ROOT ROUTES (under /pm/*)
// ============================================

export const pmRootRoutes: RouteObject[] = [
  {
    index: true,
    element: <PMWorkspaceWrapper />,
  },
  {
    path: 'projects',
    element: <PMWorkspaceWrapper canvas="projects" />,
  },
  {
    path: 'projects/:projectId',
    element: <ProjectDetailWrapper />,
  },
  {
    path: 'tasks',
    element: <PMWorkspaceWrapper canvas="tasks" />,
  },
  {
    path: 'tasks/:taskId',
    element: <TaskDetailWrapper />,
  },
]

// ============================================
// ROUTE PATHS (for navigation)
// ============================================

export const PM_ROUTES = {
  // Main
  ROOT: '/dashboard/pm',
  DASHBOARD: '/dashboard/pm',

  // Projects
  PROJECTS: '/dashboard/pm/projects',
  PROJECT_DETAIL: (id: string) => `/dashboard/pm/projects/${id}`,
  MY_PROJECTS: '/dashboard/pm/my-projects',

  // Tasks
  TASKS: '/dashboard/pm/tasks',
  TASK_DETAIL: (id: string) => `/dashboard/pm/tasks/${id}`,
  MY_TASKS: '/dashboard/pm/my-tasks',
  ASSIGNED_BY_ME: '/dashboard/pm/assigned-by-me',

  // Sprints
  SPRINTS: '/dashboard/pm/sprints',
  SPRINT_DETAIL: (id: string) => `/dashboard/pm/sprints/${id}`,
  BACKLOG: '/dashboard/pm/backlog',

  // Timeline
  MILESTONES: '/dashboard/pm/milestones',
  GANTT: '/dashboard/pm/gantt',
  TIMELINE: '/dashboard/pm/timeline',
  DEADLINES: '/dashboard/pm/deadlines',

  // Calendar
  CALENDAR: '/dashboard/pm/calendar',
  MY_CALENDAR: '/dashboard/pm/my-calendar',

  // Communication
  CHAT: '/dashboard/pm/chat',
  FEED: '/dashboard/pm/feed',

  // Resources
  RESOURCES: '/dashboard/pm/resources',
  TIME_TRACKING: '/dashboard/pm/time-tracking',

  // Admin
  CLIENTS: '/dashboard/pm/clients',
  RISKS: '/dashboard/pm/risks',
  WORKFLOWS: '/dashboard/pm/workflows',

  // Analytics
  ANALYTICS: '/dashboard/pm/analytics',

  // HR Integration (disabled via feature flag)
  // HRPM_INTEGRATION: '/dashboard/pm/hrpm-integration',
} as const
